// BRN-2123 AC3 — turns "where the operator is, right now" into the `ViewportState` the publisher
// consumes. Pure: the hook that calls it supplies the pathname and the query string, so the
// derivation is unit-testable and has no React in it.
//
// EVERY FIELD HERE COMES FROM THE OPERATOR'S OWN ADDRESS BAR OR FROM THE PRODUCT'S OWN REGISTRY —
// nothing else, and that is the answer to the security review's E2 (ELEVATION OF PRIVILEGE, HIGH).
// The concern E2 names is a snapshot carrying something the viewer's page did not render: a
// prefetched sibling, a fuller entity than the view shows, another route's slice. A derivation whose
// only inputs are the URL the operator is looking at and a static declaration cannot express that —
// there is no store, no cache and no fetcher in reach. It is a narrower snapshot than a
// store-composed one would be, deliberately: a page that later wants to describe its rendered tables
// or highlights adds a projection of WHAT IT RENDERED, and that is a change to this function's
// inputs rather than a new door into application state.

import { extractRouteParams, findSurfaceForPathname } from './route-match';
import type { ViewportSurface } from './surface-registry-types';
import type { ViewportEntityRef, ViewportFilterValue, ViewportViewModel } from './viewport-snapshot';

/** Query keys that are navigation plumbing rather than a filter the operator chose. Kept tiny and
 *  explicit: a filter list that guesses is worse than one that carries everything. */
const NON_FILTER_QUERY_KEYS = new Set(["redirect", "returnTo", "token", "code", "state"]);

export interface ViewportDerivationInput {
  readonly pathname: string;
  /** The raw query string, with or without its leading `?`. */
  readonly search: string;
}

/**
 * The rendered view model for a surface at a pathname: the route's own identifier becomes the
 * primary entity, and the query the operator can see in their address bar becomes the filters.
 */
export function deriveViewportView(surface: ViewportSurface, input: ViewportDerivationInput): ViewportViewModel {
  const entities = deriveEntities(surface, input.pathname);
  const filters = deriveFilters(input.search);
  return {
    ...(entities.length > 0 ? { entities } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
  };
}

function deriveEntities(surface: ViewportSurface, pathname: string): ViewportEntityRef[] {
  const declared = surface.routeEntityTypes;
  if (declared === undefined) {
    return [];
  }
  const params = extractRouteParams(surface.routePattern, pathname);
  // A mismatch is a registry error, not something to paper over: pairing N values with M types
  // positionally when N !== M would silently label an id with the wrong type, which is worse than
  // grounding the turn on the surface alone.
  if (params.length === 0 || params.length !== declared.length) {
    return [];
  }
  // The LAST param is the primary entity: in a nested pattern the deepest segment is what the page
  // is actually about (`/portal/sites/{facilityId}/panel/{panelId}` is a panel view, in a site).
  return params.map((id, index) => ({
    type: declared[index],
    id,
    role: index === params.length - 1 ? ("primary" as const) : ("secondary" as const),
  }));
}

function deriveFilters(search: string): Record<string, ViewportFilterValue> {
  const query = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const filters: Record<string, ViewportFilterValue> = {};
  // `forEach` rather than `for...of query.entries()`: the Angular host's tsconfig declares
  // `lib: ["es2020", "dom"]` without `dom.iterable`, where the iterator form does not type-check.
  // The three hosts run the same derivation, so it is written the way all three can compile.
  query.forEach((value, key) => {
    if (NON_FILTER_QUERY_KEYS.has(key) || value.length === 0) {
      return;
    }
    // A repeated key keeps its FIRST value rather than being concatenated: the envelope's filter
    // values are scalars, and a joined string would read to a model as one odd value.
    if (!(key in filters)) {
      filters[key] = value;
    }
  });
  return filters;
}

/**
 * The whole `ViewportState` for one URL: which declared surface the operator is on, and the view
 * model derived from that URL. `url` may carry a query string; anything after `#` is ignored, since
 * a fragment is a scroll position rather than a view.
 *
 * Returns a state with an empty view when the product declares no surface for the path — the
 * publisher turns that into one CLEARING push rather than a message with a route in it, which is
 * why this function never invents a surface for an undeclared page.
 */
export function deriveViewportState(
  url: string,
  surfaces: readonly ViewportSurface[],
): { pathname: string; view: ViewportViewModel } {
  const queryAt = url.indexOf("?");
  const pathname = queryAt === -1 ? url : url.slice(0, queryAt);
  const search = queryAt === -1 ? "" : url.slice(queryAt);
  const surface = findSurfaceForPathname(surfaces, pathname);
  return {
    pathname,
    view: surface === null ? {} : deriveViewportView(surface, { pathname, search }),
  };
}
